import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, CSSProperties, Dispatch, FormEvent, SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import type { AppDispatch } from '/business/shared/state/store';
import styled from 'styled-components';

import type { QuestionConfigQuestionType } from '/index';
import type {
  ElectricityHeatSteamConsumptionData,
  ElectricityHeatSteamConsumptionDataPayload,
  ElectricityHeatSteamLocation,
  FlagType,
} from '/business/shared/state/selectors/application/electricityHeatSteam/electHeatSteamTypes';

import formatTonnage from '/shared/functions/formatTonnage';
import isNumeric from '/shared/functions/isNumeric';
import type { NumericInput } from '/shared/functions/isNumeric';

import { urlApplicationRoot } from '/business/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import { QUESTION_BRANCH_ACTIVITY } from '/shared/util/Statics';
import getElectCertificatesSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectCertificatesSelector';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getElectLocationsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectLocationsSelector';
import getElectConsumptionDataSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectConsumptionDataSelector';
import saveConsumptionData from '/business/shared/state/actions/basket/package2/saveConsumptionData';
import useGetWasMetaSaveSuccessful from '/business/shared/components/questions/scope2/_hooks/useGetWasMetaSaveSuccessful';
import isApplicationAnswersEditableSelector from '/business/shared/state/selectors/application/workflow/isApplicationAnswersEditableSelector';

import Button from '/shared/components/widgets/button/Button';
import LinkButton from '/shared/components/widgets/button/LinkButton';
import Input from '/shared/components/widgets/input/Input';
import Select from '/shared/components/widgets/select/Select';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import EducationWidget from '/business/shared/components/widgets/education/EducationWidget';
import WidgetAccordion from '/shared/components/widgets/widget-accordion/WidgetAccordion';
import WidgetIcon from '/business/shared/images/icons/expander-icon.svg';

import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledRadio from '/shared/styled/components/StyledRadio';
import StyledTextarea from '/shared/styled/components/StyledTextarea';
import StyledFormLabel from '/shared/styled/form/StyledFormLabel';

import StyledElecFormRow from './components/__styled__/StyledElecFormRow';
import StyledElecFormButtons from './components/__styled__/StyledElecFormButtons';
import StyledElecFormSection from './components/__styled__/StyledElecFormSection';
import {
  StyledMultiStepRadioGroup,
  StyledMultiStepSummaryBody,
  StyledMultiStepSummaryButtons,
  StyledMultiStepSummaryCard,
  StyledMultiStepSummaryForm,
  StyledMultiStepSummaryHeader,
  StyledMultiStepSummaryItem,
  StyledMultiStepSummaryTitle,
} from './components/__styled__/StyledMultiStepElements';
import useGetIsFlagTypeAvailable from '/business/shared/components/questions/scope2/_hooks/useGetIsFlagTypeAvailable';
import useValidateCertificateDateRange from '/business/shared/components/questions/scope2/electricityHeatSteam/useValidateCertificateDateRange';
import Colours from '/shared/styled/Colours';

const StyledSummaryActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const StyledCertificateAssignments = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

type ConsumptionFormState = Omit<
  Partial<ElectricityHeatSteamConsumptionDataPayload>,
  'activity_quantity' | 'supplier_quantity' | 'supplier_intensity_data'
> & {
  activity_quantity?: number | string | null;
  supplier_quantity?: number | string | null;
  supplier_intensity_data?: number | string | null;
};
type EnergyType = ElectricityHeatSteamConsumptionData['flag_type'];
type EnergyUnit = 'kwh' | 'mwh';
type IntensityUnit = 'kg' | 'tonne';
type ConsumptionDataType = 'activity' | 'supplier';
type CertificateAssignment = {
  certificateId: number;
  appliedEnergy: number | null;
  appliedEnergyUnit: EnergyUnit;
  inputValue: string;
  isEditing: boolean;
};

const convertToKwh = (value: number, unit?: EnergyUnit | null) => {
  if (unit === 'mwh') {
    return value * 1000;
  }
  return value;
};

const convertFromKwh = (value: number, unit?: EnergyUnit | null) => {
  if (unit === 'mwh') {
    return value / 1000;
  }
  return value;
};

const energyUnitDropdownItems = [
  { name: 'kwh', displayName: 'kWh' },
  { name: 'mwh', displayName: 'MWh' },
];

const intensityDropdownItems = [
  { name: 'kg', displayName: 'kg CO₂e / unit' },
  { name: 'tonne', displayName: 'tCO₂e / unit' },
];

const energyTypeOptions: { id: EnergyType; labelKey: string }[] = [
  { id: 'electricity', labelKey: 'questions:electricityHeatSteam.consumptionAdd.energyTypes.electricity' },
  { id: 'heating', labelKey: 'questions:electricityHeatSteam.consumptionAdd.energyTypes.heat' },
  { id: 'steam', labelKey: 'questions:electricityHeatSteam.consumptionAdd.energyTypes.steam' },
];

const consumptionDataOptions: { id: ConsumptionDataType; labelKey: string; descriptionKey: string }[] = [
  {
    id: 'activity',
    labelKey: 'questions:electricityHeatSteam.consumptionAdd.consumptionDataTypes.activity.label',
    descriptionKey: 'questions:electricityHeatSteam.consumptionAdd.consumptionDataTypes.activity.description',
  },
  {
    id: 'supplier',
    labelKey: 'questions:electricityHeatSteam.consumptionAdd.consumptionDataTypes.supplier.label',
    descriptionKey: 'questions:electricityHeatSteam.consumptionAdd.consumptionDataTypes.supplier.description',
  },
];

const toNumberOrNull = (value: NumericInput) => (isNumeric(value) ? Number(value) : null);

const defaultFormState: ConsumptionFormState = {
  emission_type: 'absolute',
  activity_quantity_unit: 'kwh',
  supplier_quantity_unit: 'kwh',
  supplier_intensity_data_unit: 'kg',
  onsite_renewable_qty_unit: 'kwh',
  how_data_collected: '',
  supplier_name: '',
  certificates: [],
};

const consumptionListRoute = `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/consumption-data`;
const locationsAddRoute = `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/locations/locations-add`;
const certificatesAddRoute = `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/energy-attribute-certificates/energy-attribute-certificates-add`;

function FloorSpace({
  value,
  unit,
}: {
  value?: number | null;
  unit?: ElectricityHeatSteamLocation['sharedMeterTotalBuildingFloorSpaceUnit'];
}) {
  const { t } = useTranslation();
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '-';
  }
  const formatted = formatTonnage(value);
  return (
    <span>
      {formatted} {t(`business:emissionFactors.areaUnitsSm.${unit}`, unit || '-')}
    </span>
  );
}

function EnergyQuantity({ quantity, unit }: { quantity?: number | null; unit?: EnergyUnit | null }) {
  const { t } = useTranslation();
  if (typeof quantity !== 'number' || Number.isNaN(quantity)) {
    return '-';
  }
  const formatted = formatTonnage(quantity);
  return (
    <span>
      {formatted} {t(`business:emissionFactors.electricity.${unit}`, unit || '-')}
    </span>
  );
}

function IntensityValue({ intensity, unit }: { intensity?: number | null; unit?: IntensityUnit | null }) {
  const { t } = useTranslation();
  if (typeof intensity !== 'number' || Number.isNaN(intensity)) {
    return '-';
  }
  const formatted = formatTonnage(intensity);
  return (
    <span>
      {formatted} {t(`business:emissionFactors.intensityUnits.${unit}`, unit || '-')}
    </span>
  );
}

interface OnSiteRenewablesSectionProps {
  formState: ConsumptionFormState;
  updateFormField: <K extends keyof ConsumptionFormState>(field: K, value: ConsumptionFormState[K]) => void;
}

function OnSiteRenewablesSection({ formState, updateFormField }: OnSiteRenewablesSectionProps) {
  const { t } = useTranslation();
  const [onsiteStep, setOnsiteStep] = useState<'input' | 'summary'>(
    formState.onsite_renewable_qty ? 'summary' : 'input',
  );
  const [pendingOnsiteQuantity, setPendingOnsiteQuantity] = useState(formState.onsite_renewable_qty?.toString() || '');
  const [pendingOnsiteUnit, setPendingOnsiteUnit] = useState<EnergyUnit>(formState.onsite_renewable_qty_unit || 'kwh');
  const onsitePendingValue = Number(pendingOnsiteQuantity);
  const onsiteConfirmDisabled = !pendingOnsiteQuantity || Number.isNaN(onsitePendingValue) || onsitePendingValue <= 0;

  const handleConfirmOnsiteQuantity = () => {
    if (!pendingOnsiteQuantity) {
      return;
    }
    const parsedValue = Number(pendingOnsiteQuantity);
    if (Number.isNaN(parsedValue) || parsedValue <= 0) {
      return;
    }
    updateFormField('onsite_renewable_qty', parsedValue);
    updateFormField('onsite_renewable_qty_unit', pendingOnsiteUnit);
    setPendingOnsiteQuantity('');
    setOnsiteStep('summary');
  };

  const handleEditOnsiteQuantity = () => {
    setPendingOnsiteQuantity(formState.onsite_renewable_qty?.toString() || '');
    setPendingOnsiteUnit(formState.onsite_renewable_qty_unit || 'kwh');
    setOnsiteStep('input');
  };

  const handleRemoveOnsiteQuantity = () => {
    updateFormField('onsite_renewable_qty', null);
    updateFormField('onsite_renewable_qty_unit', 'kwh');
    setPendingOnsiteQuantity('');
    setPendingOnsiteUnit('kwh');
    setOnsiteStep('input');
  };

  return (
    <StyledElecFormSection data-test-id="onsite-renewables-section">
      <StyledHeading as="h2" $type="h2">
        {/* @i18n: On-site renewables (optional) */}
        <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.onsiteRenewables.title" />
      </StyledHeading>
      <StyledParagraph $smallText $hasExtraMarginBottom>
        {/* @i18n: Enter the amount of electricity consumed from on-site renewable sources during the[...] */}
        <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.onsiteRenewables.body" />
      </StyledParagraph>
      {onsiteStep === 'summary' && formState.onsite_renewable_qty ? (
        <StyledMultiStepSummaryCard data-test-id="onsite-renewables-summary">
          <StyledMultiStepSummaryHeader>
            <StyledMultiStepSummaryTitle>
              <StyledHeading as="h3" $type="h3" $semibold $noMargin>
                {/* @i18n: On-site renewable energy consumed */}
                <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.onsiteRenewables.summaryTitle" />
              </StyledHeading>
            </StyledMultiStepSummaryTitle>
            <StyledMultiStepSummaryButtons>
              <Button type="button" $secondary $small onClick={handleEditOnsiteQuantity}>
                {t('questions:electricityHeatSteam.shared.edit')}
              </Button>
              <Button type="button" $secondary $small onClick={handleRemoveOnsiteQuantity}>
                {t('questions:electricityHeatSteam.shared.remove')}
              </Button>
            </StyledMultiStepSummaryButtons>
          </StyledMultiStepSummaryHeader>
          <StyledMultiStepSummaryBody>
            <StyledMultiStepSummaryItem>
              <div>{t('questions:electricityHeatSteam.consumptionAdd.labels.quantityWithColon')}</div>
              <div>
                <EnergyQuantity
                  quantity={formState.onsite_renewable_qty}
                  unit={formState.onsite_renewable_qty_unit || 'kwh'}
                />
              </div>
            </StyledMultiStepSummaryItem>
          </StyledMultiStepSummaryBody>
        </StyledMultiStepSummaryCard>
      ) : (
        <>
          <StyledElecFormRow>
            <label htmlFor="onsite-quantity">
              {t('questions:electricityHeatSteam.consumptionAdd.labels.quantity')}
            </label>
            <Input
              testId="onsite-quantity"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.000001"
              placeholder="0"
              value={pendingOnsiteQuantity}
              onChange={(event) => setPendingOnsiteQuantity(event.target.value)}
              dropdownItems={energyUnitDropdownItems}
              dropdownValue={pendingOnsiteUnit}
              onDropdownChange={(event) => setPendingOnsiteUnit((event.target.value as EnergyUnit) || 'kwh')}
            />
          </StyledElecFormRow>
          <Button type="button" $primary onClick={handleConfirmOnsiteQuantity} disabled={onsiteConfirmDisabled}>
            {t('questions:electricityHeatSteam.shared.saveQuantity')}
          </Button>
        </>
      )}
    </StyledElecFormSection>
  );
}

interface CertificateAssignmentItemProps {
  assignment: CertificateAssignment;
  relevantCurrentAssignment?: CertificateAssignment;
  setCertificateAssignments: Dispatch<SetStateAction<CertificateAssignment[]>>;
}

function CertificateAssignmentItem({
  assignment,
  relevantCurrentAssignment,
  setCertificateAssignments,
}: CertificateAssignmentItemProps) {
  const { t } = useTranslation();
  const certificates = useSelector(getElectCertificatesSelector);

  const getCertificateById = (certificateId: number) =>
    certificates.find((certificate) => certificate.id === certificateId) || null;

  const handleCertificateInputChange = (certificateId: number, value: string) => {
    setCertificateAssignments((prev) =>
      prev.map((assignment) =>
        assignment.certificateId === certificateId
          ? {
              ...assignment,
              inputValue: value,
            }
          : assignment,
      ),
    );
  };

  const handleCertificateUnitChange = (certificateId: number, unit: EnergyUnit) => {
    setCertificateAssignments((prev) =>
      prev.map((assignment) =>
        assignment.certificateId === certificateId
          ? {
              ...assignment,
              appliedEnergyUnit: unit,
            }
          : assignment,
      ),
    );
  };

  const handleCertificateSave = (certificateId: number) => {
    setCertificateAssignments((prev) =>
      prev.map((assignment) => {
        if (assignment.certificateId !== certificateId) {
          return assignment;
        }
        const certificate = getCertificateById(certificateId);
        if (!certificate) {
          return assignment;
        }
        const parsedValue = Number(assignment.inputValue);
        const remainingKwh = convertToKwh(certificate.remaining_energy, certificate.remaining_energy_unit);
        const appliedKwh = convertToKwh(parsedValue, assignment.appliedEnergyUnit);
        if (Number.isNaN(parsedValue) || parsedValue <= 0 || appliedKwh > remainingKwh) {
          return assignment;
        }
        return {
          ...assignment,
          appliedEnergy: parsedValue,
          inputValue: parsedValue.toString(),
          isEditing: false,
        };
      }),
    );
  };

  const handleCertificateEdit = (certificateId: number) => {
    setCertificateAssignments((prev) =>
      prev.map((assignment) =>
        assignment.certificateId === certificateId
          ? {
              ...assignment,
              isEditing: true,
              inputValue: assignment.appliedEnergy?.toString() || assignment.inputValue || '',
            }
          : assignment,
      ),
    );
  };

  const handleCertificateCancelEdit = (certificateId: number) => {
    setCertificateAssignments((prev) =>
      prev.flatMap((assignment) => {
        if (assignment.certificateId !== certificateId) {
          return [assignment];
        }
        if (assignment.appliedEnergy === null) {
          return [];
        }
        return [
          {
            ...assignment,
            isEditing: false,
            inputValue: assignment.appliedEnergy.toString(),
          },
        ];
      }),
    );
  };

  const handleCertificateRemove = (certificateId: number) => {
    setCertificateAssignments((prev) => prev.filter((assignment) => assignment.certificateId !== certificateId));
  };

  const certificate = getCertificateById(assignment.certificateId);
  if (!certificate) {
    return null;
  }

  const allocationUnit = certificate.energy_allocation_for_this_carbon_review_unit;
  const remainingUnit = certificate.remaining_energy_unit;
  const allocationKwh = certificate.energy_allocation_for_this_carbon_review_kwh;

  const parsedInput = Number(assignment.inputValue);
  const previewAppliedKwh = convertToKwh(parsedInput, assignment.appliedEnergyUnit);

  const alreadySavedKwh = convertToKwh(
    relevantCurrentAssignment?.appliedEnergy || 0,
    relevantCurrentAssignment?.appliedEnergyUnit || 'kwh',
  );
  let remainingKwh =
    (certificate?.energy_allocation_for_this_carbon_review_kwh || 0) - (certificate.total_kwh_used_by_locations || 0);
  const remainingExcAlrdySave = remainingKwh;
  if (alreadySavedKwh !== previewAppliedKwh) {
    remainingKwh += alreadySavedKwh;
  }
  const alreadyAssignedKwh = Math.max((allocationKwh || 0) - remainingKwh, 0);
  const parsedInputInvalid = Number.isNaN(parsedInput) || parsedInput <= 0;
  const exceedsRemaining = !parsedInputInvalid && previewAppliedKwh > remainingKwh;

  const saveDisabled = parsedInputInvalid || exceedsRemaining;

  /*
  Fiddly as we need to exclude the current entry for validation (otherwise it's double counted)
   */
  const remainingAfterAssignmentKwh =
    assignment.isEditing && !saveDisabled
      ? Math.max(remainingExcAlrdySave, 0)
      : Math.max(remainingExcAlrdySave - previewAppliedKwh + alreadySavedKwh, 0);
  const inputId = `certificate-quantity-${assignment.certificateId}`;

  return (
    <StyledMultiStepSummaryCard
      key={`certificate-summary-${assignment.certificateId}`}
      data-test-id="certificate-assignment-summary"
    >
      <StyledMultiStepSummaryHeader>
        <StyledMultiStepSummaryTitle>
          <StyledHeading as="h3" $type="h3" $semibold $noMargin>
            {certificate.certificate_name}
          </StyledHeading>
        </StyledMultiStepSummaryTitle>
        <StyledMultiStepSummaryButtons>
          <Button type="button" $secondary $small onClick={() => handleCertificateRemove(assignment.certificateId)}>
            {t('questions:electricityHeatSteam.shared.remove')}
          </Button>
        </StyledMultiStepSummaryButtons>
      </StyledMultiStepSummaryHeader>
      <StyledMultiStepSummaryBody>
        <StyledMultiStepSummaryItem>
          <div>{t('questions:electricityHeatSteam.consumptionAdd.certificateAssignment.certificateName')}</div>
          <div>{certificate.certificate_name}</div>
        </StyledMultiStepSummaryItem>
        <StyledMultiStepSummaryItem>
          <div>{t('questions:electricityHeatSteam.consumptionAdd.certificateAssignment.allocatedEnergy')}</div>
          <div>
            <EnergyQuantity quantity={certificate.energy_allocation_for_this_carbon_review} unit={allocationUnit} />
          </div>
        </StyledMultiStepSummaryItem>
        <StyledMultiStepSummaryItem>
          <div>{t('questions:electricityHeatSteam.consumptionAdd.certificateAssignment.alreadyAssigned')}</div>
          <div>
            <EnergyQuantity quantity={convertFromKwh(alreadyAssignedKwh, allocationUnit)} unit={allocationUnit} />
          </div>
        </StyledMultiStepSummaryItem>
        <StyledMultiStepSummaryItem>
          <div>{t('questions:electricityHeatSteam.consumptionAdd.certificateAssignment.remainingAvailable')}</div>
          <div>
            <EnergyQuantity quantity={convertFromKwh(remainingKwh, remainingUnit)} unit={remainingUnit} />
          </div>
        </StyledMultiStepSummaryItem>
      </StyledMultiStepSummaryBody>
      {assignment.isEditing ? (
        <StyledMultiStepSummaryForm data-test-id="certificate-assignment-form">
          <StyledElecFormRow>
            <label htmlFor={inputId}>{t('questions:electricityHeatSteam.consumptionAdd.labels.quantity')}</label>
            <Input
              id={inputId}
              testId="certificate-quantity-input"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.000001"
              placeholder="0"
              value={assignment.inputValue}
              onChange={(event) => handleCertificateInputChange(assignment.certificateId, event.target.value)}
              dropdownItems={energyUnitDropdownItems}
              dropdownValue={assignment.appliedEnergyUnit}
              onDropdownChange={(event) =>
                handleCertificateUnitChange(assignment.certificateId, (event.target.value as EnergyUnit) || 'kwh')
              }
            />
          </StyledElecFormRow>
          <StyledSummaryActions>
            <Button
              type="button"
              $primary
              disabled={saveDisabled}
              onClick={() => handleCertificateSave(assignment.certificateId)}
            >
              {t('questions:electricityHeatSteam.shared.saveQuantity')}
            </Button>
            <Button type="button" $secondary onClick={() => handleCertificateCancelEdit(assignment.certificateId)}>
              {t('business:Buttons.cancel')}
            </Button>
          </StyledSummaryActions>
        </StyledMultiStepSummaryForm>
      ) : (
        <StyledMultiStepSummaryForm>
          <StyledMultiStepSummaryButtons>
            <Button type="button" $secondary $small onClick={() => handleCertificateEdit(assignment.certificateId)}>
              {t('questions:electricityHeatSteam.shared.edit')}
            </Button>
          </StyledMultiStepSummaryButtons>
          <StyledMultiStepSummaryBody>
            <StyledMultiStepSummaryItem>
              <div>{t('questions:electricityHeatSteam.consumptionAdd.certificateAssignment.kwhToApply')}</div>
              <div>
                <EnergyQuantity quantity={assignment.appliedEnergy} unit={assignment.appliedEnergyUnit} />
              </div>
            </StyledMultiStepSummaryItem>
            <StyledMultiStepSummaryItem>
              <div>
                {t('questions:electricityHeatSteam.consumptionAdd.certificateAssignment.remainingAfterAssignment')}
              </div>
              <div>
                <EnergyQuantity
                  quantity={convertFromKwh(remainingAfterAssignmentKwh, remainingUnit)}
                  unit={remainingUnit}
                />
              </div>
            </StyledMultiStepSummaryItem>
          </StyledMultiStepSummaryBody>
        </StyledMultiStepSummaryForm>
      )}
    </StyledMultiStepSummaryCard>
  );
}

interface ApplyCertificatesSectionProps {
  certificateAssignments: CertificateAssignment[];
  currentCertificateAssignments: CertificateAssignment[];

  setCertificateAssignments: Dispatch<SetStateAction<CertificateAssignment[]>>;
}

function ApplyCertificatesSection({
  certificateAssignments,
  setCertificateAssignments,
  currentCertificateAssignments,
}: ApplyCertificatesSectionProps) {
  const { t } = useTranslation();
  const appId = useSelector(getApplicationIdSelector);
  const { getUrlWithAppId } = useUrlBuilder();
  const certificates = useSelector(getElectCertificatesSelector);
  const validateIsDateRangeValid = useValidateCertificateDateRange();
  const [selectedCertificateId, setSelectedCertificateId] = useState('');

  const certificateOptions = useMemo(
    () =>
      certificates
        .filter((cert) => {
          if (!cert.id) {
            return false;
          }
          return validateIsDateRangeValid(cert);
        })
        .map((cert) => ({
          value: cert.id as number,
          label: cert.certificate_name,
        })),
    [certificates, validateIsDateRangeValid],
  );

  const handleCertificateChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setCertificateAssignments((prev) => [
      ...prev,
      {
        certificateId: Number(event.target.value),
        appliedEnergy: null,
        appliedEnergyUnit: 'kwh',
        inputValue: '',
        isEditing: true,
      },
    ]);

    setSelectedCertificateId('');
  };

  const availableCertificateOptions = useMemo(
    () =>
      certificateOptions.filter(
        (option) => !certificateAssignments.some((assignment) => assignment.certificateId === option.value),
      ),
    [certificateOptions, certificateAssignments],
  );

  return (
    <StyledElecFormSection>
      <StyledHeading as="h2" $type="h2">
        {/* @i18n: Apply energy attribute certificates */}
        <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.applyCertificates.title" />
      </StyledHeading>
      <StyledParagraph $smallText $hasExtraMarginBottom>
        {/* @i18n: If your business has purchased energy attribute certificates that apply to this[...] */}
        <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.applyCertificates.body" />
      </StyledParagraph>
      <StyledElecFormRow>
        <label htmlFor="certificate-select">
          {t('questions:electricityHeatSteam.consumptionAdd.applyCertificates.selectLabel')}
        </label>
        <div>
          <Select
            id="certificate-select"
            disabled={availableCertificateOptions.length === 0}
            value={selectedCertificateId}
            onChange={handleCertificateChange}
          >
            <option value="">
              {availableCertificateOptions.length === 0
                ? certificateOptions.length === 0
                  ? t('questions:electricityHeatSteam.consumptionAdd.applyCertificates.noCertificates')
                  : t('questions:electricityHeatSteam.consumptionAdd.applyCertificates.allCertificatesAdded')
                : t('questions:electricityHeatSteam.consumptionAdd.applyCertificates.selectPlaceholder')}
            </option>
            {availableCertificateOptions.map((cert) => (
              <option key={cert.value} value={cert.value}>
                {cert.label}
              </option>
            ))}
          </Select>
          <div style={{ textAlign: 'right' }}>
            <LinkButton $secondary $small to={getUrlWithAppId(certificatesAddRoute, appId)}>
              {/* @i18n: + Add a new energy attribute certificate */}
              <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.applyCertificates.addNewCertificate" />
            </LinkButton>
          </div>
        </div>
      </StyledElecFormRow>
      {certificateAssignments.length > 0 && (
        <StyledCertificateAssignments>
          {certificateAssignments.map((assignment) => {
            const relevantCurrentAssignment = currentCertificateAssignments.find(
              (curr) => curr.certificateId === assignment.certificateId,
            );
            return (
              <CertificateAssignmentItem
                key={`certificate-assignment-item-${assignment.certificateId}`}
                assignment={assignment}
                relevantCurrentAssignment={relevantCurrentAssignment}
                setCertificateAssignments={setCertificateAssignments}
              />
            );
          })}
        </StyledCertificateAssignments>
      )}
    </StyledElecFormSection>
  );
}

interface ConsumptionDataSummaryProps {
  type: ConsumptionDataType;
  formState: ConsumptionFormState;
  onEdit: (type: ConsumptionDataType) => void;
}

function ConsumptionDataSummary({ type, formState, onEdit }: ConsumptionDataSummaryProps) {
  const { t } = useTranslation();
  const title = t(
    type === 'activity'
      ? 'questions:electricityHeatSteam.consumptionAdd.consumptionDataTypes.activity.label'
      : 'questions:electricityHeatSteam.consumptionAdd.consumptionDataTypes.supplier.label',
  );
  const activityQuantityNumber = toNumberOrNull(formState.activity_quantity);
  const supplierQuantityNumber = toNumberOrNull(formState.supplier_quantity);
  const supplierIntensityNumber = toNumberOrNull(formState.supplier_intensity_data);
  const summaryItems =
    type === 'activity'
      ? [
          {
            label: t('questions:electricityHeatSteam.consumptionAdd.labels.quantityWithColon'),
            value: <EnergyQuantity quantity={activityQuantityNumber} unit={formState.activity_quantity_unit} />,
          },
          {
            label: t('questions:electricityHeatSteam.consumptionAdd.labels.howCollectedWithColon'),
            value:
              formState.how_data_collected
                ?.trim()
                .split('\n')
                .map((line, index) => <div key={index}>{line}</div>) || '-',
          },
        ]
      : [
          {
            label: t('questions:electricityHeatSteam.consumptionAdd.labels.supplierNameWithColon'),
            value: formState.supplier_name?.trim() || '-',
          },
          {
            label: t('questions:electricityHeatSteam.consumptionAdd.labels.supplierIntensityWithColon'),
            value: <IntensityValue intensity={supplierIntensityNumber} unit={formState.supplier_intensity_data_unit} />,
          },
          {
            label: t('questions:electricityHeatSteam.consumptionAdd.labels.quantityWithColon'),
            value: <EnergyQuantity quantity={supplierQuantityNumber} unit={formState.supplier_quantity_unit} />,
          },
          {
            label: t('questions:electricityHeatSteam.consumptionAdd.labels.howCollectedWithColon'),
            value:
              formState.how_data_collected
                ?.trim()
                .split('\n')
                .map((line, index) => <div key={index}>{line}</div>) || '-',
          },
        ];

  return (
    <StyledMultiStepSummaryCard data-test-id="consumption-data-summary">
      <StyledMultiStepSummaryHeader>
        <StyledMultiStepSummaryTitle>
          <StyledHeading as="h3" $type="h3" $semibold $noMargin>
            {title}
          </StyledHeading>
        </StyledMultiStepSummaryTitle>
        <StyledMultiStepSummaryButtons>
          <Button type="button" $secondary $small onClick={() => onEdit(type)}>
            {t('questions:electricityHeatSteam.shared.edit')}
          </Button>
        </StyledMultiStepSummaryButtons>
      </StyledMultiStepSummaryHeader>
      <StyledMultiStepSummaryBody>
        {summaryItems.map((item) => (
          <StyledMultiStepSummaryItem key={`${type}-${item.label}`}>
            <div>{item.label}</div>
            <div>{item.value}</div>
          </StyledMultiStepSummaryItem>
        ))}
      </StyledMultiStepSummaryBody>
    </StyledMultiStepSummaryCard>
  );
}

interface LocationSectionProps {
  selectedLocation: ElectricityHeatSteamLocation | null;
  locationStep: 'select' | 'confirm' | 'complete';
  handleLocationChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onRemove: () => void;
}

function LocationSection({ selectedLocation, locationStep, handleLocationChange, onRemove }: LocationSectionProps) {
  const { t } = useTranslation();
  const appId = useSelector(getApplicationIdSelector);
  const { getUrlWithAppId } = useUrlBuilder();
  const locations = useSelector(getElectLocationsSelector);
  const isSummaryVisible = Boolean(selectedLocation && locationStep !== 'select');
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isFlagTypeAvailable = useGetIsFlagTypeAvailable(editId);
  const isEditMode = Boolean(editId);
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  return (
    <StyledElecFormSection>
      <StyledHeading as="h2" $type="h2">
        {/* @i18n: Which location are you inputting consumption data for? */}
        <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.locationSection.title" />
      </StyledHeading>
      {locationStep === 'select' && (
        <StyledElecFormRow>
          <label htmlFor="consumption-location-select">
            {t('questions:electricityHeatSteam.consumptionAdd.locationSection.selectLabel')}
          </label>
          <div>
            <Select
              disabled={!isEditable}
              id="consumption-location-select"
              value={selectedLocation?.id || ''}
              onChange={handleLocationChange}
            >
              <option value="">
                {locations.length > 0
                  ? t('questions:electricityHeatSteam.consumptionAdd.locationSection.selectPlaceholder')
                  : t('questions:electricityHeatSteam.consumptionAdd.locationSection.noLocations')}
              </option>
              {locations
                .filter((location) => location.id)
                .map((location) => {
                  const isDisabledLocation =
                    [
                      isFlagTypeAvailable(location, 'heating', !isEditMode),
                      isFlagTypeAvailable(location, 'steam', !isEditMode),
                      isFlagTypeAvailable(location, 'electricity', !isEditMode),
                    ].filter((prop) => {
                      return !prop;
                    }).length === 3;
                  return (
                    <option key={location.id} value={location.id} disabled={isDisabledLocation}>
                      {location.unique_name}
                    </option>
                  );
                })}
            </Select>
            <div style={{ textAlign: 'right' }}>
              <LinkButton $secondary $small to={getUrlWithAppId(locationsAddRoute, appId)}>
                {/* @i18n: + Add a new location */}
                <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.locationSection.addNewLocation" />
              </LinkButton>
            </div>
          </div>
        </StyledElecFormRow>
      )}

      {isSummaryVisible && selectedLocation && (
        <>
          <StyledMultiStepSummaryCard data-test-id="location-summary">
            <StyledMultiStepSummaryHeader>
              <StyledMultiStepSummaryTitle>
                <StyledHeading as="h3" $type="h3" $semibold $noMargin>
                  {/* @i18n: Location summary */}
                  <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.locationSection.summaryTitle" />
                </StyledHeading>
              </StyledMultiStepSummaryTitle>
              <StyledMultiStepSummaryButtons>
                <Button type="button" $secondary $small onClick={onRemove}>
                  {t('questions:electricityHeatSteam.shared.remove')}
                </Button>
              </StyledMultiStepSummaryButtons>
            </StyledMultiStepSummaryHeader>
            <StyledMultiStepSummaryBody>
              <StyledMultiStepSummaryItem>
                <div>{t('questions:electricityHeatSteam.consumptionAdd.locationSection.locationName')}</div>
                <div>{selectedLocation.unique_name}</div>
              </StyledMultiStepSummaryItem>
              <StyledMultiStepSummaryItem>
                <div>{t('questions:electricityHeatSteam.consumptionAdd.locationSection.businessOccupies')}</div>
                <div>
                  {t(
                    selectedLocation.isWholeBuilding
                      ? 'questions:electricityHeatSteam.locationsAdd.summary.wholeLocation'
                      : 'questions:electricityHeatSteam.locationsAdd.summary.partOfLocation',
                  )}
                </div>
              </StyledMultiStepSummaryItem>
              <StyledMultiStepSummaryItem>
                <div>{t('questions:electricityHeatSteam.consumptionAdd.locationSection.energyRecorded')}</div>
                <div>
                  {t(
                    selectedLocation.isOwnMeter
                      ? 'questions:electricityHeatSteam.consumptionAdd.locationSection.dedicatedMeter'
                      : 'questions:electricityHeatSteam.consumptionAdd.locationSection.sharedMeter',
                  )}
                </div>
              </StyledMultiStepSummaryItem>
              {Boolean(selectedLocation.sharedMeterTotalBuildingFloorSpace) && (
                <StyledMultiStepSummaryItem>
                  <div>{t('questions:electricityHeatSteam.locationsAdd.labels.totalFloorSpace')}</div>
                  <div>
                    <FloorSpace
                      value={selectedLocation.sharedMeterTotalBuildingFloorSpace}
                      unit={selectedLocation.sharedMeterTotalBuildingFloorSpaceUnit}
                    />
                  </div>
                </StyledMultiStepSummaryItem>
              )}
              {Boolean(selectedLocation.sharedMeterBusinessOnlyFloorSpace) && (
                <StyledMultiStepSummaryItem>
                  <div>{t('questions:electricityHeatSteam.locationsAdd.labels.businessFloorSpace')}</div>
                  <div>
                    <FloorSpace
                      value={selectedLocation.sharedMeterBusinessOnlyFloorSpace}
                      unit={selectedLocation.sharedMeterBusinessOnlyFloorSpaceUnit}
                    />
                  </div>
                </StyledMultiStepSummaryItem>
              )}
            </StyledMultiStepSummaryBody>
          </StyledMultiStepSummaryCard>
          {selectedLocation.id && (
            <StyledParagraph $smallText $hasExtraMarginBottom>
              <LinkButton
                $secondary
                $small
                $inline
                to={getUrlWithAppId(`${locationsAddRoute}?edit=${selectedLocation.id}`, appId)}
              >
                {/* @i18n: Edit this location information */}
                <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.locationSection.editLocation" />
              </LinkButton>
              {/* only show this text if not on location confirmation */}
              {locationStep !== 'confirm' && (
                <div>
                  {/* @i18n: Any information added below will not be saved until you finish this form. */}
                  <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.locationSection.unsavedInformation" />
                </div>
              )}
            </StyledParagraph>
          )}
        </>
      )}
    </StyledElecFormSection>
  );
}

function AlreadyInUseSection({ selectedLocation }: { selectedLocation: ElectricityHeatSteamLocation | null }) {
  const { t } = useTranslation();
  const isFlagTypeAvailable = useGetIsFlagTypeAvailable(null);
  if (!selectedLocation) {
    return null;
  }
  const hse: FlagType[] = ['heating', 'steam', 'electricity'];
  const inUseTypes = hse.filter((type) => {
    return !isFlagTypeAvailable(selectedLocation, type, true);
  });

  if (inUseTypes.length === 0) {
    return null;
  }
  const inUseReadable = inUseTypes
    .map((type) => t(`questions:electricityHeatSteam.consumptionAdd.energyTypes.${type === 'heating' ? 'heat' : type}`))
    .join(` ${t('questions:electricityHeatSteam.shared.and')} `);
  return (
    <StyledInfoFlash style={{ marginBottom: '-1rem', marginTop: '-4rem' }}>
      <div>
        <StyledParagraph $smallText $bold>
          {/* @i18n: You already have electricity consumption data saved for this location */}
          <Trans
            i18nKey="questions:electricityHeatSteam.consumptionAdd.alreadyInUse.title"
            values={{ inUseReadable }}
          />
        </StyledParagraph>
        <StyledParagraph $smallText>
          {/* @i18n: If you would like to add more consumption data of the same energy type, you will need[...] */}
          <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.alreadyInUse.body" />
        </StyledParagraph>
      </div>
    </StyledInfoFlash>
  );
}

export default function ElectricityHeatSteamConsumptionAdd({ question }: { question: QuestionConfigQuestionType }) {
  const { t } = useTranslation();
  const appId = useSelector(getApplicationIdSelector);
  const { getUrlWithAppId } = useUrlBuilder();
  const locations = useSelector(getElectLocationsSelector);
  const consumptionData = useSelector(getElectConsumptionDataSelector);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = Boolean(editId);
  const isFlagTypeAvailable = useGetIsFlagTypeAvailable(editId);
  const wasSaveSuccessful = useGetWasMetaSaveSuccessful();

  const currentConsumptionData = isEditMode
    ? consumptionData.find((data) => data.id?.toString() === editId) || null
    : null;

  const currentConsumptionDataType = currentConsumptionData
    ? currentConsumptionData.emission_type === 'absolute'
      ? 'activity'
      : 'supplier'
    : null;

  const [formState, setFormState] = useState<ConsumptionFormState>(currentConsumptionData || defaultFormState);

  const [consumptionDataType, setConsumptionDataType] = useState<ConsumptionDataType | null>(
    currentConsumptionDataType,
  );
  const [consumptionDataSelectedType, setConsumptionDataSelectedType] = useState<ConsumptionDataType | null>(
    currentConsumptionDataType,
  );
  const [consumptionAccordionDefaultExpanded, setConsumptionAccordionDefaultExpanded] =
    useState<ConsumptionDataType | null>(null);

  const [locationStep, setLocationStep] = useState<'select' | 'confirm' | 'complete'>(
    isEditMode ? 'complete' : 'select',
  );
  const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>(formState.location_id);
  const [selectedEnergyType, setSelectedEnergyType] = useState<EnergyType | null>(formState.flag_type ?? null);

  const currentCertificateAssignments: CertificateAssignment[] = useMemo(() => {
    return (
      formState.certificates?.map((certificate) => ({
        certificateId: certificate.certificate_id as number,
        appliedEnergy: certificate.applied_energy,
        appliedEnergyUnit: certificate.applied_energy_unit || 'kwh',
        inputValue: certificate.applied_energy?.toString() || '',
        isEditing: false,
      })) || []
    );
  }, [formState]);

  const [certificateAssignments, setCertificateAssignments] =
    useState<CertificateAssignment[]>(currentCertificateAssignments);

  const selectedLocation = useMemo(() => {
    return locations.find((location) => location.id && location.id === selectedLocationId) || null;
  }, [locations, selectedLocationId]);

  const isLocationSelected = Boolean(selectedLocation);
  const isFullFormVisible = Boolean(selectedLocation && locationStep === 'complete');

  // @note: Not needed unless certificates will ever change in background whilst form is being filled in
  // useEffect(() => {
  //   setCertificateAssignments((prev) =>
  //     prev.filter((assignment) =>
  //       certificateOptions.some((option) => option.value === assignment.certificateId),
  //     ),
  //   );
  // }, [certificateOptions]);

  // @note: Not needed unless certificates will ever change in background whilst form is being filled in
  // useEffect(() => {
  //   if (!selectedCertificateId) {
  //     return;
  //   }
  //   const stillAvailable = availableCertificateOptions.some(
  //     (option) => option.value.toString() === selectedCertificateId,
  //   );
  //   if (!stillAvailable) {
  //     setSelectedCertificateId('');
  //   }
  // }, [availableCertificateOptions, selectedCertificateId]);

  const handleLocationChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const newLocationId = Number(value);
    setSelectedLocationId(newLocationId);
    setLocationStep('confirm');
    const newLocation = locations.find((location) => location.id && location.id === newLocationId) || null;
    setFormState((prev) => ({
      ...prev,
      location_id: newLocation?.id ?? undefined,
    }));
  };

  const handleLocationRemove = () => {
    setSelectedLocationId(undefined);
    setLocationStep('select');
    setSelectedEnergyType(null);
    setConsumptionDataType(null);
    setConsumptionDataSelectedType(null);
    setConsumptionAccordionDefaultExpanded(null);
    // reset form state
    setFormState(defaultFormState);
  };

  const handleConsumptionDataTypeConfirm = (mode: ConsumptionDataType) => {
    setConsumptionDataType(mode);
    setConsumptionDataSelectedType(mode);
    setConsumptionAccordionDefaultExpanded(null);
    setFormState((prev) => ({
      ...prev,
      emission_type: mode === 'activity' ? 'absolute' : 'supplier',
    }));
  };

  const handleConsumptionDataTypeEdit = (mode: ConsumptionDataType) => {
    setConsumptionDataSelectedType(null);
    setConsumptionDataType(null);
    setConsumptionAccordionDefaultExpanded(mode);
  };

  const activityQuantityNumber = toNumberOrNull(formState.activity_quantity);
  const supplierQuantityNumber = toNumberOrNull(formState.supplier_quantity);
  const supplierIntensityNumber = toNumberOrNull(formState.supplier_intensity_data);

  const activityAddDisabled = !activityQuantityNumber || activityQuantityNumber <= 0;
  const supplierAddDisabled =
    !supplierQuantityNumber || supplierQuantityNumber <= 0 || !supplierIntensityNumber || supplierIntensityNumber <= 0;

  const updateFormField = <K extends keyof ConsumptionFormState>(field: K, value: ConsumptionFormState[K]) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedLocation || !selectedEnergyType) {
      return;
    }

    let consumptionTypeKey: ElectricityHeatSteamConsumptionData['type'] = 'calc-elect-consumption-flag-elect';
    if (selectedEnergyType === 'heating') {
      consumptionTypeKey = 'calc-elect-consumption-flag-heat';
    } else if (selectedEnergyType === 'steam') {
      consumptionTypeKey = 'calc-elect-consumption-flag-steam';
    }

    const payload: ElectricityHeatSteamConsumptionDataPayload = {
      type: consumptionTypeKey,
      location_id: selectedLocation.id as number,
      flag_type: selectedEnergyType,
      emission_type: formState.emission_type ?? (consumptionDataType === 'activity' ? 'absolute' : 'supplier'),
      onsite_renewable_qty: formState.onsite_renewable_qty ?? null,
      onsite_renewable_qty_unit: formState.onsite_renewable_qty_unit ?? 'kwh',
      activity_quantity: consumptionDataType === 'activity' ? activityQuantityNumber : null,
      activity_quantity_unit: formState.activity_quantity_unit ?? 'kwh',
      supplier_intensity_data: consumptionDataType === 'supplier' ? supplierIntensityNumber : null,
      supplier_intensity_data_unit: formState.supplier_intensity_data_unit ?? 'kg',
      supplier_quantity: consumptionDataType === 'supplier' ? supplierQuantityNumber : null,
      supplier_quantity_unit: formState.supplier_quantity_unit ?? 'kwh',
      how_data_collected: formState.how_data_collected || '',
      supplier_name: formState.supplier_name || '',
      certificates: certificateAssignments
        .filter((assignment) => assignment.appliedEnergy !== null)
        .map((assignment) => ({
          certificate_id: assignment.certificateId,
          applied_energy: assignment.appliedEnergy as number,
          applied_energy_kwh: convertToKwh(assignment.appliedEnergy as number, assignment.appliedEnergyUnit),
          applied_energy_unit: assignment.appliedEnergyUnit,
        })),
    };

    await dispatch(
      saveConsumptionData({
        basketItem: {
          id: currentConsumptionData?.id || undefined,
          genericData: payload,
        },
        applicationId: appId ?? 0,
      }),
    );
  };
  const isEditable = useSelector(isApplicationAnswersEditableSelector);

  useEffect(() => {
    if (wasSaveSuccessful) {
      navigate(consumptionListRoute);
    }
  }, [wasSaveSuccessful, navigate]);

  const isSaveDisabled =
    !selectedLocation || locationStep !== 'complete' || !selectedEnergyType || !consumptionDataSelectedType;

  return (
    <>
      <EducationWidget question={question} branchType={QUESTION_BRANCH_ACTIVITY} />

      <div>
        <StyledHeading as="h1" $type="h1">
          {isEditMode ? (
            <>
              {/* @i18n: Electricity, heat and steam - Edit consumption data */}
              <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.editTitle" />
            </>
          ) : (
            <>
              {/* @i18n: Electricity, heat and steam - Add new consumption data */}
              <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.addTitle" />
            </>
          )}
        </StyledHeading>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          {/* @i18n: Enter the consumption details for any electricity, heat or steam used by your business[...] */}
          <Trans
            i18nKey="questions:electricityHeatSteam.consumptionAdd.intro"
            components={{ 1: <ReportingPeriod $green /> }}
          />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>
            {/* @i18n: Refer to the "Help" section for detailed guidance about this category and how to avoid[...] */}
            <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.helpText" />
          </div>
        </StyledInfoFlash>
      </div>

      <form onSubmit={handleSubmit}>
        {/* The "What location are you inputting consumption data for?" section */}
        <LocationSection
          selectedLocation={selectedLocation}
          locationStep={locationStep}
          handleLocationChange={handleLocationChange}
          onRemove={handleLocationRemove}
        />

        {isFullFormVisible && (
          <>
            <StyledElecFormSection>
              <StyledHeading as="h2" $type="h2">
                {/* @i18n: Please select the type of energy you want to enter for this location */}
                <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.energyTypeTitle" />
              </StyledHeading>
              <StyledMultiStepRadioGroup>
                {energyTypeOptions.map((option) => {
                  const styleProp: CSSProperties = {};
                  const lineDisabled = !isFlagTypeAvailable(selectedLocation, option.id, false);
                  if (lineDisabled) {
                    styleProp.color = Colours.greyLight5;
                  }
                  return (
                    <StyledFormLabel key={option.id} $isRow $noWrap $noBottomPadding>
                      <StyledRadio
                        name="consumption-energy-type"
                        checked={selectedEnergyType === option.id}
                        disabled={lineDisabled}
                        onChange={() => {
                          setSelectedEnergyType(option.id);
                          setFormState((prev) => ({
                            ...prev,
                            flag_type: option.id ?? undefined,
                          }));
                        }}
                      />
                      <span style={{ ...styleProp }}>{t(option.labelKey)}</span>
                    </StyledFormLabel>
                  );
                })}
              </StyledMultiStepRadioGroup>
            </StyledElecFormSection>
            <AlreadyInUseSection selectedLocation={selectedLocation} />

            <StyledElecFormSection>
              <StyledHeading as="h2" $type="h2">
                {/* @i18n: Grid consumption data */}
                <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.gridConsumptionTitle" />
              </StyledHeading>
              <StyledParagraph $smallText $hasExtraMarginBottom>
                {/* @i18n: Emissions can be estimated using standard emission factors based on your activity[...] */}
                <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.gridConsumptionBody" />
              </StyledParagraph>

              {consumptionDataSelectedType ? (
                <ConsumptionDataSummary
                  type={consumptionDataSelectedType}
                  formState={formState}
                  onEdit={handleConsumptionDataTypeEdit}
                />
              ) : (
                <div>
                  {consumptionDataOptions.map((option) => (
                    <div data-test-id={`consumption-data-option-${option.id}`} key={option.id}>
                      <WidgetAccordion
                        key={option.id}
                        defaultExpanded={consumptionAccordionDefaultExpanded === option.id}
                        items={[
                          option.id === 'activity' ? (
                            <div key="activity-content">
                              <StyledElecFormRow>
                                <label htmlFor="activity-quantity">
                                  {t('questions:electricityHeatSteam.consumptionAdd.labels.quantity')}
                                </label>
                                <Input
                                  testId="activity-quantity"
                                  type="number"
                                  inputMode="decimal"
                                  min={0}
                                  step="0.000001"
                                  value={formState.activity_quantity ?? ''}
                                  onChange={(event) => updateFormField('activity_quantity', event.target.value)}
                                  dropdownItems={energyUnitDropdownItems}
                                  dropdownValue={formState.activity_quantity_unit}
                                  onDropdownChange={(event) =>
                                    updateFormField(
                                      'activity_quantity_unit',
                                      (event.target.value as EnergyUnit) || 'kwh',
                                    )
                                  }
                                />
                              </StyledElecFormRow>
                              <StyledElecFormRow>
                                <label htmlFor="activity-how-data-collected">
                                  {t('questions:electricityHeatSteam.consumptionAdd.labels.howCollected')}
                                </label>
                                <StyledTextarea
                                  data-test-id="activity-how-data-collected"
                                  value={formState.how_data_collected || ''}
                                  placeholder={t(
                                    'questions:electricityHeatSteam.consumptionAdd.placeholders.howCollected',
                                  )}
                                  rows={4}
                                  onChange={(event) => updateFormField('how_data_collected', event.target.value)}
                                />
                              </StyledElecFormRow>
                              <Button
                                type="button"
                                $primary
                                disabled={activityAddDisabled}
                                onClick={() => handleConsumptionDataTypeConfirm('activity')}
                              >
                                {t('business:Buttons.add')}
                              </Button>
                            </div>
                          ) : (
                            <div key="supplier-content">
                              <StyledElecFormRow>
                                <label htmlFor="supplier-name">
                                  {t('questions:electricityHeatSteam.consumptionAdd.labels.supplierName')}
                                </label>
                                <Input
                                  testId="supplier-name"
                                  type="text"
                                  value={formState.supplier_name || ''}
                                  placeholder={t(
                                    'questions:electricityHeatSteam.consumptionAdd.placeholders.supplierName',
                                  )}
                                  onChange={(event) => updateFormField('supplier_name', event.target.value)}
                                />
                              </StyledElecFormRow>
                              <StyledElecFormRow>
                                <label htmlFor="supplier-intensity">
                                  {t('questions:electricityHeatSteam.consumptionAdd.labels.supplierIntensity')}
                                </label>
                                <Input
                                  testId="supplier-intensity"
                                  type="number"
                                  inputMode="decimal"
                                  min={0}
                                  step="0.000001"
                                  value={formState.supplier_intensity_data ?? ''}
                                  onChange={(event) => updateFormField('supplier_intensity_data', event.target.value)}
                                  dropdownItems={intensityDropdownItems}
                                  dropdownValue={formState.supplier_intensity_data_unit}
                                  onDropdownChange={(event) =>
                                    updateFormField(
                                      'supplier_intensity_data_unit',
                                      (event.target.value as IntensityUnit) || 'kg',
                                    )
                                  }
                                />
                              </StyledElecFormRow>
                              <StyledElecFormRow>
                                <label htmlFor="supplier-quantity">
                                  {t('questions:electricityHeatSteam.consumptionAdd.labels.quantity')}
                                </label>
                                <Input
                                  testId="supplier-quantity"
                                  type="number"
                                  inputMode="decimal"
                                  min={0}
                                  step="0.000001"
                                  value={formState.supplier_quantity ?? ''}
                                  onChange={(event) => updateFormField('supplier_quantity', event.target.value)}
                                  dropdownItems={energyUnitDropdownItems}
                                  dropdownValue={formState.supplier_quantity_unit}
                                  onDropdownChange={(event) =>
                                    updateFormField(
                                      'supplier_quantity_unit',
                                      (event.target.value as EnergyUnit) || 'kwh',
                                    )
                                  }
                                />
                              </StyledElecFormRow>
                              <StyledElecFormRow>
                                <label htmlFor="supplier-how-data-collected">
                                  {t('questions:electricityHeatSteam.consumptionAdd.labels.howCollected')}
                                </label>
                                <StyledTextarea
                                  data-test-id="supplier-how-data-collected"
                                  value={formState.how_data_collected || ''}
                                  placeholder={t(
                                    'questions:electricityHeatSteam.consumptionAdd.placeholders.howCollected',
                                  )}
                                  rows={4}
                                  onChange={(event) => updateFormField('how_data_collected', event.target.value)}
                                />
                              </StyledElecFormRow>
                              <Button
                                type="button"
                                $primary
                                disabled={supplierAddDisabled}
                                onClick={() => handleConsumptionDataTypeConfirm('supplier')}
                              >
                                {t('business:Buttons.add')}
                              </Button>
                            </div>
                          ),
                        ]}
                      >
                        <div className="text">
                          <StyledHeading as="h2" $type="h2" $semibold>
                            {t(option.labelKey)}
                          </StyledHeading>
                          <StyledParagraph $smallText>{t(option.descriptionKey)}</StyledParagraph>
                        </div>
                        <div className="icon">
                          <img src={WidgetIcon} alt={t('questions:electricityHeatSteam.consumptionAdd.expanderAlt')} />
                        </div>
                      </WidgetAccordion>
                    </div>
                  ))}
                </div>
              )}
            </StyledElecFormSection>

            {/* The "On-site renewables (optional)" section. Only when electricity data is being entered */}
            {selectedEnergyType === 'electricity' && (
              <OnSiteRenewablesSection formState={formState} updateFormField={updateFormField} />
            )}

            {/* The "Apply energy attribute certificates" section */}
            <ApplyCertificatesSection
              certificateAssignments={certificateAssignments}
              currentCertificateAssignments={currentCertificateAssignments}
              setCertificateAssignments={setCertificateAssignments}
            />
          </>
        )}

        <StyledElecFormButtons>
          {locationStep === 'select' && (
            <Button type="button" $primary onClick={() => setLocationStep('confirm')} disabled={!isLocationSelected}>
              {t('questions:electricityHeatSteam.shared.next')}
            </Button>
          )}
          {locationStep === 'confirm' && (
            <Button type="button" $primary onClick={() => setLocationStep('complete')}>
              {t('questions:electricityHeatSteam.shared.next')}
            </Button>
          )}
          {locationStep === 'complete' && (
            <Button type="submit" $primary disabled={isSaveDisabled || !isEditable}>
              {t(isEditMode ? 'business:Buttons.update' : 'business:Buttons.save')}
            </Button>
          )}
          <LinkButton $secondary to={getUrlWithAppId(consumptionListRoute, appId)}>
            {/* @i18n: Cancel and return to consumption data */}
            <Trans i18nKey="questions:electricityHeatSteam.consumptionAdd.cancelReturn" />
          </LinkButton>
        </StyledElecFormButtons>
      </form>
    </>
  );
}
